class PollClient<AsyncReturnType> {
  private pollingFunction: () => Promise<AsyncReturnType | null>;

  private pollingInterval: number;

  private pollingLimit: number;

  private continuePollingCondition: (res: AsyncReturnType) => boolean;

  private pollSuccessCallback: (res: AsyncReturnType) => void;

  private pollFailedCallback: (err: Error) => void;

  private pollExpireCallback: () => void;

  private pollCounter: number;

  private timeout: ReturnType<typeof setTimeout> | null;

  constructor() {
    this.pollingInterval = 3000;
    this.pollingLimit = 10;
    this.pollCounter = 0;
    this.timeout = null;

    this.continuePollingCondition = () => false;

    this.pollingFunction = () => Promise.resolve(null);

    this.pollSuccessCallback = () => {};

    this.pollFailedCallback = () => {};

    this.pollExpireCallback = () => {};
  }

  setPollingFunction = (func: () => Promise<AsyncReturnType>) => {
    this.pollingFunction = func;
    return this;
  };

  setPollingInterval = (delay: number) => {
    this.pollingInterval = delay;
    return this;
  };

  setPollingLimit = (maxPoll: number) => {
    this.pollingLimit = maxPoll;
    return this;
  };

  setPollSuccessCallback = (func: (res: AsyncReturnType) => void) => {
    this.pollSuccessCallback = func;
    return this;
  };

  setContinuePollingCondition = (func: (res: AsyncReturnType) => boolean) => {
    this.continuePollingCondition = func;
    return this;
  };

  setPollFailedCallback = (func: (err: Error) => void) => {
    this.pollFailedCallback = func;
    return this;
  };

  setPollExpireCallback = (func: () => void) => {
    this.pollExpireCallback = func;
    return this;
  };

  abort = () => {
    this.pollCounter = 0;
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  };

  private callApi = () => {
    if (this.pollCounter < this.pollingLimit) {
      this.pollCounter++;
      this.pollingFunction()
        .then(response => {
          const res = response as AsyncReturnType;
          const shouldContinuePoll = this.continuePollingCondition(res);

          if (shouldContinuePoll) {
            this.timeout = setTimeout(this.callApi, this.pollingInterval);
          } else {
            this.pollSuccessCallback(res);
            this.abort();
          }
        })
        .catch(err => {
          this.pollFailedCallback(err);
          this.abort();
        });
    } else {
      this.pollExpireCallback();
      this.abort();
    }
  };

  startPoll = () => {
    this.pollCounter = 0;
    this.callApi();
  };
}

export default PollClient;
